/**
 * CriteriaTab Component
 *
 * Main container for the Criteria tab in ReferenceDataPage.
 * Provides full CRUD functionality for criteria items with:
 * - List display with category badges and "used in" counts
 * - Create/Edit modal
 * - Archive/Restore actions with confirmation
 * - Show Archived toggle
 * - Filter by category (multi-select) and usage
 * - Sort options
 * - Cross-tab sync subscription
 * - Responsive: desktop inline filters, mobile bottom sheet
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Eye, EyeOff, AlertTriangle, RefreshCw, SlidersHorizontal, ArrowUp, ArrowDown } from 'lucide-react';

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  useDisclosure,
} from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks';
import { criteriaService } from '@/services/CriteriaService';
import { marketConditionCategoryService } from '@/services/MarketConditionCategoryService';
import { playbookService } from '@/services/PlaybookService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { Playbook } from '@/types/schema/Playbook';

import { CriteriaListItem } from './CriteriaListItem';
import { CriteriaModal } from './CriteriaModal';

// Filter constants
const NO_CATEGORY_KEY = '__none__';
type UsageFilter = 'all' | 'used' | 'unused';
type SortField = 'name' | 'category' | 'usage';
type SortDir = 'asc' | 'desc';

const SORT_FIELDS: { key: SortField; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'usage', label: 'Used' },
];

const USAGE_OPTIONS: { key: UsageFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'used', label: 'Used' },
  { key: 'unused', label: 'Unused' },
];

export const CriteriaTab: React.FC = () => {
  const isDesktop = useIsDesktop();

  // Data state
  const [criteria, setCriteria] = useState<PlaybookCriteriaItem[]>([]);
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [showArchived, setShowArchived] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<PlaybookCriteriaItem | undefined>();
  const [archivingCriteria, setArchivingCriteria] = useState<PlaybookCriteriaItem | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Filter/sort state
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [usageFilter, setUsageFilter] = useState<UsageFilter>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Mobile filter sheet staged state (filters only - sort is immediate via header buttons)
  const [stagedCategoryFilter, setStagedCategoryFilter] = useState<string[]>([]);
  const [stagedUsageFilter, setStagedUsageFilter] = useState<UsageFilter>('all');
  const [stagedShowArchived, setStagedShowArchived] = useState(false);

  // Portal container state for mobile filter sheet (fixes aria-hidden warning)
  // Using state instead of ref ensures re-render when container mounts
  const [mobileFilterPortalContainer, setMobileFilterPortalContainer] = useState<HTMLDivElement | null>(null);
  const setMobileFilterPortalRef = useCallback((node: HTMLDivElement | null) => {
    setMobileFilterPortalContainer(node);
  }, []);

  // Modal state
  const modalDisclosure = useDisclosure();
  const filterSheetDisclosure = useDisclosure();

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const [allCriteria, allCategories, allPlaybooks] = await Promise.all([
        criteriaService.getAll(),
        marketConditionCategoryService.getAll(),
        playbookService.getAll(),
      ]);
      setCriteria(allCriteria);
      setCategories(allCategories);
      setPlaybooks(allPlaybooks);
    } catch (error) {
      console.error('Failed to load criteria data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for criteria changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('playbook_criteria_items', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to category changes (for badge updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('market_condition_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to playbook changes (for "used in" count updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('playbooks', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build usedInMap: criteriaId -> playbookNames[] (sorted alphabetically)
  const usedInMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const playbook of playbooks) {
      if (playbook.criteriaIds) {
        for (const criteriaId of playbook.criteriaIds) {
          const names = map.get(criteriaId) || [];
          names.push(playbook.name);
          map.set(criteriaId, names);
        }
      }
    }
    // Sort each array alphabetically (case-insensitive)
    for (const [key, names] of map) {
      map.set(key, names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
    }
    return map;
  }, [playbooks]);

  // Build categoryMap for O(1) lookups
  const categoryMap = useMemo(() => {
    return new Map(categories.map(c => [c.id, c]));
  }, [categories]);

  // Active categories for dropdowns
  const activeCategories = useMemo(() => {
    return categories.filter(c => c.status !== 'archived');
  }, [categories]);

  // Check if there are any archived criteria
  const hasArchivedCriteria = useMemo(() => {
    return criteria.some(c => c.status === 'archived');
  }, [criteria]);

  // Check if any filters are active (for mobile badge - excludes sort since sort is in header)
  const hasActiveFilters = useMemo(() => {
    return categoryFilter.length > 0 || usageFilter !== 'all' || showArchived;
  }, [categoryFilter, usageFilter, showArchived]);

  // Filter and sort criteria
  const sortedCriteria = useMemo(() => {
    // Step 1: Apply archived filter FIRST
    let filtered = showArchived
      ? criteria
      : criteria.filter(c => c.status !== 'archived');

    // Step 2: Apply category filter
    if (categoryFilter.length > 0) {
      filtered = filtered.filter(c => {
        const catId = c.marketConditionCategoryId;
        if (categoryFilter.includes(NO_CATEGORY_KEY) && !catId) {
          return true;
        }
        return catId && categoryFilter.includes(catId);
      });
    }

    // Step 3: Apply usage filter
    if (usageFilter === 'used') {
      filtered = filtered.filter(c => (usedInMap.get(c.id)?.length ?? 0) > 0);
    } else if (usageFilter === 'unused') {
      filtered = filtered.filter(c => (usedInMap.get(c.id)?.length ?? 0) === 0);
    }

    // Step 4: Sort
    return [...filtered].sort((a, b) => {
      // Active items always first (above sort comparator)
      if (a.status === 'archived' && b.status !== 'archived') return 1;
      if (a.status !== 'archived' && b.status === 'archived') return -1;

      // Direction multiplier: 1 for asc, -1 for desc
      const dirMult = sortDir === 'asc' ? 1 : -1;

      // Then apply selected sort field
      switch (sortField) {
        case 'category': {
          const catA = categoryMap.get(a.marketConditionCategoryId || '')?.name || '';
          const catB = categoryMap.get(b.marketConditionCategoryId || '')?.name || '';
          const catCompare = catA.localeCompare(catB, undefined, { sensitivity: 'base' });
          if (catCompare !== 0) return catCompare * dirMult;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
        case 'usage': {
          const usageA = usedInMap.get(a.id)?.length ?? 0;
          const usageB = usedInMap.get(b.id)?.length ?? 0;
          // For usage: asc = least used first, desc = most used first
          if (usageA !== usageB) return (usageA - usageB) * dirMult;
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        }
        case 'name':
        default:
          return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) * dirMult;
      }
    });
  }, [criteria, showArchived, categoryFilter, usageFilter, sortField, sortDir, usedInMap, categoryMap]);

  // Handlers
  const handleAddClick = () => {
    setEditingCriteria(undefined);
    modalDisclosure.onOpen();
  };

  const handleEditClick = (criteriaItem: PlaybookCriteriaItem) => {
    setEditingCriteria(criteriaItem);
    modalDisclosure.onOpen();
  };

  const handleSave = async (data: { name: string; description?: string; marketConditionCategoryId?: string }) => {
    setIsSaving(true);
    try {
      if (editingCriteria) {
        await criteriaService.update(editingCriteria.id, data);
        syncCoordinatorService.notifyChange('playbook_criteria_items', editingCriteria.id);
      } else {
        const newCriteria = await criteriaService.add(data);
        syncCoordinatorService.notifyChange('playbook_criteria_items', newCriteria.id);
      }
      await loadData();
      modalDisclosure.onClose();
    } catch (error) {
      console.error('Failed to save criteria:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveClick = (criteriaItem: PlaybookCriteriaItem) => {
    setArchivingCriteria(criteriaItem);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingCriteria) return;
    try {
      await criteriaService.update(archivingCriteria.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('playbook_criteria_items', archivingCriteria.id);
      await loadData();
    } catch (error) {
      console.error('Failed to archive criteria:', error);
    } finally {
      setArchivingCriteria(undefined);
    }
  };

  const handleArchiveCancel = () => {
    setArchivingCriteria(undefined);
  };

  const handleRestore = async (criteriaItem: PlaybookCriteriaItem) => {
    try {
      await criteriaService.update(criteriaItem.id, { status: 'active' });
      syncCoordinatorService.notifyChange('playbook_criteria_items', criteriaItem.id);
      await loadData();
    } catch (error) {
      console.error('Failed to restore criteria:', error);
    }
  };

  // Mobile filter sheet handlers (sort is excluded - it's immediate via header buttons)
  const handleOpenFilterSheet = () => {
    // Stage current filter values
    setStagedCategoryFilter(categoryFilter);
    setStagedUsageFilter(usageFilter);
    setStagedShowArchived(showArchived);
    filterSheetDisclosure.onOpen();
  };

  const handleApplyFilters = () => {
    setCategoryFilter(stagedCategoryFilter);
    setUsageFilter(stagedUsageFilter);
    setShowArchived(stagedShowArchived);
    filterSheetDisclosure.onClose();
  };

  const handleResetFilters = () => {
    setStagedCategoryFilter([]);
    setStagedUsageFilter('all');
    setStagedShowArchived(false);
  };

  // Sort button handler: toggle direction if same field, switch if different
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card radius="none" shadow="none">
        <CardBody className="px-4 py-4">
          <div className="text-center py-8">
            <p className="text-text-secondary">Loading criteria...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Card radius="none" shadow="none">
        <CardBody className="px-4 py-4">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="flex items-center gap-2 text-danger-600">
              <AlertTriangle size={20} />
              <p className="text-sm">Failed to load criteria</p>
            </div>
            <Button
              size="sm"
              variant="flat"
              startContent={<RefreshCw size={16} />}
              onPress={loadData}
            >
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Empty state
  if (criteria.length === 0) {
    return (
      <>
        <Card radius="none" shadow="none">
          <CardBody className="px-4 py-4">
            <EmptyState
              title="No criteria yet"
              description="Criteria are hard filters — every one must be met for the playbook to be valid. Add criteria to define what conditions must exist before you consider a trade."
              action={
                <Button
                  variant="light"
                  startContent={<Plus size={16} />}
                  onPress={handleAddClick}
                >
                  Add Criteria
                </Button>
              }
            />
          </CardBody>
        </Card>
        <CriteriaModal
          isOpen={modalDisclosure.isOpen}
          onClose={modalDisclosure.onClose}
          criteria={editingCriteria}
          categories={activeCategories}
          allCategories={categories}
          onSave={handleSave}
          isSaving={isSaving}
        />
      </>
    );
  }

  // Render filter UI based on device
  const renderDesktopFilters = () => (
    <div className="flex flex-wrap items-center justify-between w-full gap-3">
      {/* Filters Group */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-tertiary mr-1">Filters:</span>
        {/* Category Filter (multi-select) */}
        <Select
          size="sm"
          variant="bordered"
          placeholder="Category"
          aria-label="Filter by category"
          selectionMode="multiple"
          selectedKeys={new Set(categoryFilter)}
          onSelectionChange={(keys) => setCategoryFilter(Array.from(keys) as string[])}
          className="min-w-30"
          popoverProps={{ isNonModal: true, placement: 'bottom-start' }}
        >
          {[
            <SelectItem key={NO_CATEGORY_KEY} textValue="No Category">
              <span className="text-text-secondary">No Category</span>
            </SelectItem>,
            ...activeCategories.map((cat) => (
              <SelectItem key={cat.id} textValue={cat.name}>
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: cat.color }}
                  />
                  <span>{cat.name}</span>
                </div>
              </SelectItem>
            )),
          ]}
        </Select>

        {/* Usage Filter - Segmented Buttons */}
        <div className="flex items-center border border-default-200 rounded-lg">
          {USAGE_OPTIONS.map((opt) => (
            <Button
              key={opt.key}
              size="sm"
              variant="light"
              radius="none"
              onPress={() => setUsageFilter(opt.key)}
              className={
                usageFilter === opt.key
                  ? 'bg-default-100 text-primary font-medium flex-1'
                  : 'text-text-secondary flex-1'
              }
            >
              {opt.label}
            </Button>
          ))}
        </div>

        {/* Archived Toggle */}
        {hasArchivedCriteria && (
          <Button
            size="sm"
            variant="flat"
            startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
            onPress={() => setShowArchived(!showArchived)}
            className={`bg-default-100 shrink-0 min-w-max ${  showArchived ? 'text-text-primary' : 'text-text-tertiary'}`}
          >
            Archived
          </Button>
        )}
      </div>

      {/* Sort Buttons */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-text-tertiary mr-1">Sort:</span>
        {SORT_FIELDS.map((field) => (
          <Button
            key={field.key}
            size="sm"
            variant="light"
            onPress={() => handleSortClick(field.key)}
            className={
              sortField === field.key
                ? 'bg-default-100 text-primary font-medium'
                : 'text-text-secondary'
            }
            endContent={
              sortField === field.key ? (
                sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
              ) : undefined
            }
          >
            {field.label}
          </Button>
        ))}
      </div>

      {/* Add Button */}
      <Button
        variant="light"
        size="sm"
        startContent={<Plus size={16} />}
        onPress={handleAddClick}
        className="shrink-0"
      >
        Add Criteria
      </Button>
    </div>
  );

  const renderMobileFilters = () => (
    <div className="flex items-center justify-between w-full gap-2">
      {/* Filter Icon Button with badge */}
      <Button
        size="sm"
        variant="light"
        isIconOnly
        onPress={handleOpenFilterSheet}
        aria-label="Filter"
        className="relative shrink-0"
      >
        <SlidersHorizontal size={16} />
        {hasActiveFilters && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-primary rounded-full" />
        )}
      </Button>

      {/* Sort Buttons - same as desktop */}
      <div className="flex items-center gap-1 flex-1 justify-center">
        {SORT_FIELDS.map((field) => (
          <Button
            key={field.key}
            size="sm"
            variant="light"
            onPress={() => handleSortClick(field.key)}
            className={
              sortField === field.key
                ? 'bg-default-100 text-primary font-medium px-2'
                : 'text-text-secondary px-2'
            }
            endContent={
              sortField === field.key ? (
                sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
              ) : undefined
            }
          >
            {field.label}
          </Button>
        ))}
      </div>

      <Button
        variant="light"
        size="sm"
        startContent={<Plus size={16} />}
        onPress={handleAddClick}
        className="shrink-0"
      >
        Add
      </Button>
    </div>
  );

  return (
    <>
      {/* Subtitle - above filters */}
      <p
        className="text-xs text-text-secondary truncate px-4 py-2"
        title="Conditions that must be met when applied to a playbook"
      >
        Conditions that must be met when applied to a playbook
      </p>

      <Card radius="none" shadow="none">
        {/* Header with filters */}
        <CardHeader className="py-3 border-b border-default-200">
          {isDesktop ? renderDesktopFilters() : renderMobileFilters()}
        </CardHeader>

        <CardBody className="px-0 py-4">
          {/* Criteria List */}
          {sortedCriteria.length > 0 && (
            <div className="space-y-2">
              {sortedCriteria.map((criteriaItem) => (
                <Card
                  key={criteriaItem.id}
                  radius="sm"
                  shadow="none"
                  className="border border-default-200"
                >
                  <CardBody className="p-0">
                    <CriteriaListItem
                      criteria={criteriaItem}
                      category={categoryMap.get(criteriaItem.marketConditionCategoryId || '')}
                      playbookNames={usedInMap.get(criteriaItem.id) || []}
                      onEdit={() => handleEditClick(criteriaItem)}
                      onArchiveClick={() => handleArchiveClick(criteriaItem)}
                      onRestore={() => handleRestore(criteriaItem)}
                    />
                  </CardBody>
                </Card>
              ))}
            </div>
          )}

          {/* No results from filters */}
          {sortedCriteria.length === 0 && criteria.length > 0 && (
            <EmptyState
              title="No matching criteria"
              description={
                hasActiveFilters
                  ? "No criteria match the current filters. Try adjusting your filters."
                  : "All criteria are archived. Enable 'Archived' to view them."
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <CriteriaModal
        isOpen={modalDisclosure.isOpen}
        onClose={modalDisclosure.onClose}
        criteria={editingCriteria}
        categories={activeCategories}
        allCategories={categories}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={!!archivingCriteria}
        onOpenChange={(open) => !open && handleArchiveCancel()}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>Archive Criteria</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              Are you sure you want to archive &quot;{archivingCriteria?.name}&quot;?
            </p>
            <p className="text-sm text-text-tertiary">
              Archived criteria remain functional in playbooks that reference them.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleArchiveCancel}>
              Cancel
            </Button>
            <Button color="warning" onPress={handleArchiveConfirm}>
              Archive
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mobile Filter Sheet - filters only, sort is in header */}
      <Modal
        isOpen={filterSheetDisclosure.isOpen}
        onOpenChange={(open) => !open && filterSheetDisclosure.onClose()}
        size="full"
        placement="bottom"
        classNames={{
          wrapper: 'items-end',
          base: 'rounded-t-xl rounded-b-none max-h-[60vh]',
        }}
      >
        <ModalContent>
          <ModalHeader className="flex justify-between items-center">
            <span>Filter</span>
            <Button size="sm" variant="light" onPress={handleResetFilters}>
              Reset
            </Button>
          </ModalHeader>
          <ModalBody className="gap-4">
            <div ref={setMobileFilterPortalRef} className="flex flex-col gap-4">
              {/* Category Filter */}
              <Select
                label="Category"
                variant="bordered"
                selectionMode="multiple"
                selectedKeys={new Set(stagedCategoryFilter)}
                onSelectionChange={(keys) => setStagedCategoryFilter(Array.from(keys) as string[])}
                popoverProps={{
                  isNonModal: true,
                  shouldBlockScroll: false,
                  placement: 'bottom-start',
                  ...(mobileFilterPortalContainer ? { portalContainer: mobileFilterPortalContainer } : {}),
                }}
              >
                {[
                  <SelectItem key={NO_CATEGORY_KEY} textValue="No Category">
                    <span className="text-text-secondary">No Category</span>
                  </SelectItem>,
                  ...activeCategories.map((cat) => (
                    <SelectItem key={cat.id} textValue={cat.name}>
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span>{cat.name}</span>
                      </div>
                    </SelectItem>
                  )),
                ]}
              </Select>

              {/* Usage Filter - Segmented Buttons */}
              <div className="space-y-2">
                <span className="text-sm text-text-secondary">Usage</span>
                <div className="flex items-center border border-default-200 rounded-lg overflow-hidden">
                  {USAGE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.key}
                      size="sm"
                      variant="light"
                      radius="none"
                      onPress={() => setStagedUsageFilter(opt.key)}
                      className={
                        stagedUsageFilter === opt.key
                          ? 'bg-default-100 text-primary font-medium flex-1'
                          : 'text-text-secondary flex-1'
                      }
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Show Archived Toggle */}
              {hasArchivedCriteria && (
                <Button
                  size="sm"
                  variant="flat"
                  startContent={stagedShowArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                  onPress={() => setStagedShowArchived(!stagedShowArchived)}
                  className={stagedShowArchived ? 'bg-default-100 text-text-primary' : 'bg-default-100 text-text-tertiary'}
                >
                  Archived
                </Button>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={filterSheetDisclosure.onClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleApplyFilters}>
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
